/**
 * Share links of one share project (Phase 6), for its manager or an admin.
 * A link shows this project's published templates to anyone who has it, with
 * no sign-in. The full link is shown once, when it is made.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import type { NewShareLink, ProjectDetail, ShareLink, ShareLinkState } from '../api/types';
import { Badge, Button, ErrorBox, Field, formatDateTime, Modal, Select, TableCard, TextInput } from '../components/ui';
import { shareUrl } from './paths';

const STATE: Record<ShareLinkState, { label: string; tone: 'green' | 'slate' | 'red' | 'amber' }> = {
  active: { label: 'working', tone: 'green' },
  expired: { label: 'expired', tone: 'slate' },
  revoked: { label: 'switched off', tone: 'red' },
  used_up: { label: 'download limit reached', tone: 'amber' },
};

const DURATIONS = [[7, '1 week'], [30, '1 month'], [90, '3 months'], [365, '1 year']] as const;

/** The new link, once: copy it now, it cannot be shown again. */
function NewLinkModal({ made, onClose }: { made: NewShareLink | null; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  const url = made ? shareUrl(made.token) : '';
  return (
    <Modal open={made !== null} title="Your new share link" onClose={() => { setCopied(false); onClose(); }}>
      <div className="flex flex-col gap-3 text-sm">
        <p>Send this link to {made?.link.label ? <b>{made.link.label}</b> : 'the people you share with'}. They open it without signing in and download the templates as PDFs.</p>
        <TextInput readOnly value={url} aria-label="The share link" className="w-full font-mono text-xs" onFocus={e => e.currentTarget.select()} data-testid="new-share-url" />
        <div className="flex flex-wrap items-center gap-2">
          <Button tone="primary" onClick={() => void navigator.clipboard?.writeText(url).then(() => setCopied(true), () => setCopied(false))}>Copy link</Button>
          {copied && <span className="text-green-800" role="status">Copied.</span>}
        </div>
        <p className="rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-950">
          Copy it now: for safety the full link is shown only this once. If it is lost, press <b>New address</b> on it.
        </p>
        <div className="flex justify-end"><Button onClick={() => { setCopied(false); onClose(); }}>Done</Button></div>
      </div>
    </Modal>
  );
}

export function ShareLinksSection({ data }: { data: ProjectDetail }) {
  const { project, templates } = data;
  const qc = useQueryClient();
  const key = ['share-links', project.id];
  const links = useQuery({ queryKey: key, queryFn: () => api<{ links: ShareLink[] }>('GET', `/api/projects/${project.id}/share-links`) });
  const [label, setLabel] = useState('');
  const [days, setDays] = useState<number>(30);
  const [limit, setLimit] = useState('');
  const [made, setMade] = useState<NewShareLink | null>(null);
  const done = (r?: NewShareLink) => { void qc.invalidateQueries({ queryKey: key }); if (r) setMade(r); };

  const create = useMutation({
    mutationFn: () => api<NewShareLink>('POST', `/api/projects/${project.id}/share-links`, {
      body: { ...(label.trim() ? { label: label.trim() } : {}), expiresInDays: days, maxDownloads: limit ? Number(limit) : null },
    }),
    onSuccess: r => { setLabel(''); setLimit(''); done(r); },
  });
  const revoke = useMutation({
    mutationFn: (l: ShareLink) => api('POST', `/api/projects/${project.id}/share-links/${l.id}/revoke`),
    onSuccess: () => done(),
  });
  const replace = useMutation({
    mutationFn: (l: ShareLink) => api<NewShareLink>('POST', `/api/projects/${project.id}/share-links/${l.id}/replace`),
    onSuccess: r => done(r),
  });
  const list = links.data?.links ?? [];
  const limitOk = limit === '' || (/^\d+$/.test(limit) && Number(limit) >= 1 && Number(limit) <= 10_000);

  return (
    <section aria-labelledby="share-heading" className="flex flex-col gap-2" data-testid="share-links">
      <h2 id="share-heading" className="text-lg font-semibold">Share links ({list.filter(l => l.state === 'active').length} working)</h2>
      <p className="text-xs text-slate-500">
        Anyone with a link sees the templates above (their latest published version) and can download them as PDFs, without signing in.
        Never documents or anyone's details. Each link stops on its date, at its download limit, or when you switch it off.
      </p>
      {templates.length === 0 && <p className="text-sm text-amber-900">Add a template above first: a link shows this project's templates.</p>}
      <ErrorBox error={links.error ?? revoke.error ?? replace.error} />
      {list.length > 0 && (
        <TableCard>
          <caption className="sr-only">Share links of this project</caption>
          <thead>
            <tr><th scope="col">For</th><th scope="col">Link</th><th scope="col">State</th><th scope="col">Works until</th><th scope="col">Downloads</th><th scope="col">Opened</th><th scope="col"><span className="sr-only">Actions</span></th></tr>
          </thead>
          <tbody>
            {list.map(l => (
              <tr key={l.id} data-link={l.id}>
                <td>{l.label ?? <span className="text-slate-500">—</span>}<span className="block text-xs text-slate-500">made {formatDateTime(l.createdAt)} by {l.createdBy}</span></td>
                <td><code className="text-xs">…/share#{l.prefix}…</code></td>
                <td><Badge tone={STATE[l.state].tone}>{STATE[l.state].label}</Badge></td>
                <td className="text-xs">{formatDateTime(l.expiresAt)}</td>
                <td>{l.downloads}{l.maxDownloads !== null && ` of ${l.maxDownloads}`}</td>
                <td className="text-xs">{l.views} time{l.views === 1 ? '' : 's'}{l.lastUsedAt && <span className="block text-slate-500">last {formatDateTime(l.lastUsedAt)}</span>}{l.denied > 0 && <span className="block text-amber-800">{l.denied} refused</span>}</td>
                <td className="whitespace-nowrap text-right">
                  {l.state !== 'revoked' && (
                    <Button tone="ghost" disabled={replace.isPending}
                      title="A new address for the same people. The old address stops working at once."
                      onClick={() => { if (confirm('Make a new address for this link? The old address stops working at once.')) replace.mutate(l); }}>New address</Button>
                  )}
                  {l.state === 'active' && (
                    <Button tone="ghost" className="ml-1" disabled={revoke.isPending}
                      onClick={() => { if (confirm('Switch this link off? It stops working at once, for good.')) revoke.mutate(l); }}>Switch off</Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </TableCard>
      )}
      <form className="flex flex-wrap items-end gap-2" onSubmit={e => { e.preventDefault(); if (limitOk) create.mutate(); }}>
        <Field label="For (optional)">{fid => <TextInput id={fid} value={label} onChange={e => setLabel(e.target.value)} maxLength={120} placeholder="e.g. ABC Builders" />}</Field>
        <Field label="Works for">{fid => (
          <Select id={fid} value={String(days)} onChange={e => setDays(Number(e.target.value))}>
            {DURATIONS.map(([d, text]) => <option key={d} value={d}>{text}</option>)}
          </Select>
        )}</Field>
        <Field label="Download limit (optional)" error={limitOk ? undefined : 'A whole number from 1 to 10000'}>{(fid, d) => (
          <TextInput id={fid} aria-describedby={d} inputMode="numeric" value={limit} onChange={e => setLimit(e.target.value.trim())} placeholder="no limit" className="w-32" />
        )}</Field>
        <Button tone="primary" type="submit" disabled={create.isPending || !limitOk}>Make a share link</Button>
      </form>
      <ErrorBox error={create.error} />
      <NewLinkModal made={made} onClose={() => setMade(null)} />
    </section>
  );
}
